'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import { type PriceAlertTriggerDto, usePriceAlertTriggers } from '@/lib/api/priceAlerts';
import { PriceAlertEmptyState } from './PriceAlertEmptyState';
import { PriceAlertHistoryCard } from './PriceAlertHistoryCard';
import { PriceAlertHistoryRow } from './PriceAlertHistoryRow';

interface PriceAlertHistoryViewProps {
  enabled: boolean;
}

export function PriceAlertHistoryView({ enabled }: PriceAlertHistoryViewProps) {
  const t = useTranslations('priceAlerts');
  const tHist = useTranslations('priceAlerts.history');

  const query = usePriceAlertTriggers({}, { enabled });
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  const triggers: PriceAlertTriggerDto[] = useMemo(
    () => data?.pages.flatMap((p) => p.content) ?? [],
    [data],
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="size-6 animate-spin text-v2-text-secondary" />
      </div>
    );
  }

  if (isError) {
    return <PriceAlertEmptyState message={t('list.error')} />;
  }

  if (triggers.length === 0) {
    return <PriceAlertEmptyState message={t('emptyHistory')} />;
  }

  return (
    <>
      {/* Mobile (<600px): card list */}
      <div className="flex flex-col gap-2 min-[600px]:hidden">
        {triggers.map((trigger) => (
          <PriceAlertHistoryCard key={trigger.id} trigger={trigger} />
        ))}
      </div>

      {/* Desktop (≥600px): grid table */}
      <div className="hidden grid-cols-[minmax(0,1fr)_auto_120px] items-center gap-x-2 min-[600px]:grid">
        {/* Header */}
        <div className="col-span-full grid grid-cols-subgrid items-center gap-x-2 border-b border-v2-border px-2 pb-2">
          <span className="text-[10px] leading-none text-v2-text-secondary">
            {tHist('triggeredAlert')}
          </span>
          <span className="text-right text-[10px] leading-none text-v2-text-secondary">
            {tHist('time')}
          </span>
          <span className="text-right text-[10px] leading-none text-v2-text-secondary">
            {tHist('status')}
          </span>
        </div>

        {/* Rows */}
        {triggers.map((trigger) => (
          <PriceAlertHistoryRow key={trigger.id} trigger={trigger} />
        ))}
      </div>

      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center py-3" aria-hidden>
          {isFetchingNextPage && <Loader2 className="size-5 animate-spin text-v2-text-secondary" />}
        </div>
      )}
    </>
  );
}
